import { Accordion, AccordionSummary, AccordionDetails, Button, Container, Divider, FormControl, ToggleButton, ToggleButtonGroup, Typography, Tooltip, CircularProgress, Input } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { EconomicsSetting } from "./EconomicsSetting";

import ResetIcon from '@mui/icons-material/SettingsBackupRestore'; 
import { useDispatch, useSelector } from "react-redux";

import { ExpandMore, Launch, PlayArrow } from "@mui/icons-material";

import { selectEconomics, setCurrentPriceDeck } from "../reducers/economicsSlice";
import { useEffect, useMemo, useState } from "react";

import { selectUser } from "../reducers/userSlice";
import { EditPrices } from "./EditPrices";

import { useSocket } from "../context/useSocket";
import { selectOptions, setIsLoadingData } from "../reducers/optionsSlice";
import { getSourceData } from "./EditEconomics";
import { selectLists } from "../reducers/listSlice";
import { selectWells } from "../reducers/wellSlice";
import { selectTypeCurves } from "../reducers/typeCurveSlice";
import { selectGroupWells } from "../reducers/groupWellSlice";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'transparent',
    width: '100%',
    height: 'calc(100vh)',
    overflowY: 'auto',
    padding: 0,
    paddingBottom: 140
  },
  header: {
    margin: 10
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 10px',
    marginBottom: 20,
  },
  toggleButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10
  },
  cutoff: {
    padding: '0 10px'
  },
  emptyForecast: {
    height: 'calc(100% - 120px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  details: {
    margin: 0,
    padding: 0
  }
}));

export const EconomicsSettings = ({ economicsSettings, setEconomicsSettings }) => {
  
  const { socket } = useSocket();
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const options = useSelector(selectOptions);
  const economics = useSelector(selectEconomics);
  const currentEconomics = economics?.byId?.[economics?.currentEconomicsId];
  const isRollupEconomics = currentEconomics?.economicsIds?.length > 0;
  const lists = useSelector(selectLists);
  const wells = useSelector(selectWells);
  const typeCurves = useSelector(selectTypeCurves);
  const groupWells = useSelector(selectGroupWells);

  const darkMode = user?.value?.darkMode;

  const hasUnprocessedWells = useMemo(() => {
    const unprocessedWells = [];
    currentEconomics?.economicsWells?.wells?.forEach(well => {
      if (wells?.byUwi?.[well.id]?.status !== 'processed') {
        unprocessedWells.push(well.id);
      };
    });

    return unprocessedWells?.length > 0;
  }, [currentEconomics?.economicsWells?.wells, wells?.byUwi]);

  const grouping = useMemo(() => {
    return ([
      {
        name: 'Prices',
        parameters: [
          'priceDeckName', 
          'priceOil', 
          'priceGas', 
          'priceCondensate', 
          'pricePropane', 
          'priceButane',
          'pricePentanes',
          'mcfToMmbtu', 
          'priceOffsetOil', 
          'priceOffsetGas', 
          'priceOffsetCondensate',
          'priceOffsetPropane',
          'priceOffsetButane',
          'priceOffsetPentanes'
        ]
      },
      {
        name: 'Natural Gas Liquids',
        parameters: [
          'nglGasShrinkage',
          'nglPropaneYield',
          'nglButaneYield',
          'nglPentanesYield'
        ]
      },
      {
        name: 'Costs',
        parameters: [
          'opexVariableOil', 
          'opexVariableGas', 
          'opexVariableCondensate', 
          'opexVariableWater', 
          'opexFixed', 
          'abandonment',
          'abandonmentDelay', 
          'royalty',
          'adValorem'
        ]
      },
      {
        name: 'Escalation',
        parameters: ['priceEscalation', 'costEscalation']
      },
      {
        name: 'Ownership',
        parameters: ['ownership', 'ownershipCost', 'ownershipCapex']
      },
      {
        name: 'Cutoff',
        parameters: ['cutoffMethod', 'discountRate']
      },
      {
        name: 'Tax Schedule',
        parameters: ['country', 'provinceOrState', 'tangibleCapital', 'intangibleCapital', 'salvageValue', 'ggDeduction', 'taxRate', 'localTaxRate']
      }
    ]);
  }, []);

  const [editPricesVisible, setEditPricesVisible] = useState(false);
  const [expandAll, setExpandAll] = useState(true);
  const [expanded, setExpanded] = useState(grouping.map(g => g.name));

  const [economicsUnits, setEconomicsUnits] = useState(economicsSettings?.units?.length > 0 ? economicsSettings?.units : 'field');
  const [economicsCurrency, setEconomicsCurrency] = useState(economicsSettings?.currency?.length > 0 ? economicsSettings?.currency : 'cad');
  const [economicsStartDate, setEconomicsStartDate] = useState(economicsSettings?.startDate || '');

  const [setUwis] = useState(economics?.economicsWells?.wells?.map(well => well.id) || []);

  const cadToUsd = economicsSettings?.priceOil?.find(p => Number(new Date(p.date)?.getFullYear()) === currentEconomics?.cashflowGross?.[0]?.year)?.cadToUsd;


  useEffect(() => {
    if (expandAll) setExpanded(grouping.map(g => g.name));
  }, [grouping, expandAll]);

  useEffect(() => {
    if (currentEconomics?.settings) setEconomicsSettings(currentEconomics?.settings);
  }, [currentEconomics?.settings, setEconomicsSettings]);

  useEffect(() => {
    setEconomicsCurrency(currentEconomics?.settings?.currency);
    setEconomicsStartDate(currentEconomics?.settings?.startDate || '');
  }, [currentEconomics]);

  try {
    return (
      <Container className={classes.root}>

        {editPricesVisible && <EditPrices handleEditPricesClose={() => setEditPricesVisible(false)} economics={currentEconomics} />}

        {(economicsSettings && currentEconomics?.settings && currentEconomics?.cashflowNet && currentEconomics?.cashflowGross) ?
        <div style={{width: '100%', height: '100%'}}>
          <div style={{position: 'fixed', zIndex: 69, backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'], width: '299px'}}>

            <div className={classes.header}>
              <Typography variant="subtitle2" color="textSecondary" noWrap>
                {currentEconomics?.name || ""}
              </Typography>
              <Typography variant="h6" className={classes.heading}>
                Economics Forecast Settings
              </Typography>
            </div>
        
            <div className={classes.buttons}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<ResetIcon />} 
                className={classes.button} 
                size='small' 
                onClick={e => handleResetEconomics(e)}
                disabled={currentEconomics?.lastSavedSettings === economicsSettings && currentEconomics?.lastSavedSettings?.startDate === economicsStartDate}
              >
                Reset
              </Button>
              <div style={{position: 'relative'}}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<PlayArrow />} 
                  className={classes.button} 
                  size='small' 
                  onClick={() => handleCalculateEconomics(economicsSettings)}
                  disabled={(currentEconomics?.settings === economicsSettings && currentEconomics?.settings?.startDate === economicsStartDate) || hasUnprocessedWells}
                >
                  Calculate
                </Button>
                {options?.isLoadingData && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-11px',
                      marginLeft: '40px',
                    }}
                  />
                )}
              </div>
            </div>

            <FormControl component="fieldset" style={{width: '100%', padding: '0 10px'}}>
              <Typography gutterBottom noWrap>
                Currency
              </Typography>
              <ToggleButtonGroup
                value={economicsCurrency}
                exclusive
                onChange={handleCurrencyChange}
                size="small"
                style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 20}}
              >
                <ToggleButton value="cad" size="small" style={{width: '50%'}} color="primary">
                  <Typography style={{fontSize: 12}}>
                    CAD
                  </Typography>
                </ToggleButton>
                <ToggleButton value="usd" size="small" style={{width: '50%'}} color="primary">
                  <Typography style={{fontSize: 12}}>
                    USD
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            <FormControl component="fieldset" style={{width: '100%', padding: '0 10px'}}>
              <Typography gutterBottom noWrap>
                Units
              </Typography>
              <ToggleButtonGroup
                value={economicsUnits}
                exclusive
                onChange={handleUnitsChange}
                size="small"
                style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 20}}
                disabled
              >
                <ToggleButton value="metric" size="small" style={{width: '50%'}} color="primary">
                  <Typography style={{fontSize: 12}}>
                    Metric
                  </Typography>
                </ToggleButton>
                <ToggleButton value="field" size="small" style={{width: '50%'}} color="primary">
                  <Typography style={{fontSize: 12}}>
                    Field
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            {!isRollupEconomics &&<FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginBottom: 20}}>
              <Typography gutterBottom noWrap>
                Start Date
              </Typography>
              <Input type="date" value={economicsStartDate} onChange={e => setEconomicsStartDate(e.target.value)}></Input>
            </FormControl>}

            {!isRollupEconomics && 
            <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
              <Button size="small" onClick={handleExpandAll}><Typography fontSize={10}>{expandAll ? 'Collapse' : 'Expand'} All</Typography></Button>
            </div>}

            {!isRollupEconomics && <Divider />}
          </div>

          {!isRollupEconomics && 
          <div style={{position: 'absolute', top: 490, width: '299px', height: 'calc(100% - 490px)', overflowY: 'scroll'}}>
            {grouping.map(group => {
              return (
                <div className={classes.settings} key={group?.name}>
                  <Accordion 
                    expanded={expanded.indexOf(group?.name) !== -1}
                    elevation={0}
                    style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />} onClick={() => handleExpand(group?.name)}>
                      <Tooltip title={group?.name}><Typography className={classes.heading} noWrap>{group?.name || ""}</Typography></Tooltip>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                      {
                        group?.name === 'Prices' &&
                        <div style={{display: 'flex', justifyContent: 'center', margin: '0 10px'}}>
                          <Button size="small" startIcon={<Launch />} color="primary" variant="outlined" onClick={() => setEditPricesVisible(true)} fullWidth>Edit Prices</Button>
                        </div>
                      }
                      {group?.parameters?.map(param => {
                        return (
                          <div key={param}>
                            <EconomicsSetting 
                              parameter={param} 
                              handlePriceDeckChange={handlePriceDeckChange} 
                              economicsSettings={economicsSettings} 
                              setEconomicsSettings={setEconomicsSettings}
                              cadToUsd={cadToUsd}
                            />
                            <Divider style={{width: '100%'}}/>
                          </div>
                        )
                      })}
                    </AccordionDetails>
                  </Accordion>
                  <Divider />
                </div>
              )
            })}
          </div>}
        </div> 
        :
        <div className={classes.emptyForecast}>
          <Typography variant = "h6" color="textSecondary" style={{marginTop: 20}}>Settings not available</Typography>
        </div>}
      </Container>
    );
    
  
    // FUNCTIONS
    async function handleResetEconomics(event) {
      event.stopPropagation();

      let sourceData = null;
      let economicsAnalyses = null;
      let newEconomics = currentEconomics;

      if (currentEconomics?.economicsWells && Object.keys(currentEconomics?.economicsWells)?.length > 0) {
        const compiledResult = await compileNewEconomics(currentEconomics, currentEconomics?.lastSavedSettings);
        sourceData = compiledResult?.sourceData;
        newEconomics = compiledResult?.economics;  
      } else if (currentEconomics?.economicsIds?.length > 0) {
        currentEconomics?.economicsIds.forEach(economicsId => {
          if (economics?.byId?.[economicsId]?.cashflowNet && economics?.byId?.[economicsId]?.forecastNet) {
            if (!economicsAnalyses) {
              economicsAnalyses = [];
            };
            economicsAnalyses.push({
              cashflowNet: economics?.byId?.[economicsId]?.cashflowNet, 
              cashflowByFluidNet: economics?.byId?.[economicsId]?.cashflowByFluidNet, 
              cashflowGross: economics?.byId?.[economicsId]?.cashflowGross, 
              cashflowByFluidGross: economics?.byId?.[economicsId]?.cashflowByFluidGross, 
              forecastNet: economics?.byId?.[economicsId]?.forecastNet,
              forecastCompanyNet: economics?.byId?.[economicsId]?.forecastCompanyNet,
              forecastGross: economics?.byId?.[economicsId]?.forecastGross,
              settings: economics?.byId?.[economicsId]?.settings
            });
          };
        });
      };

      if (socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: 'economics', 
          payload: {sourceData, economics: {...newEconomics, settings: currentEconomics?.lastSavedSettings}, economicsAnalyses}, 
          userEmail: user?.value?.email
        }));
      };
      setEconomicsSettings(currentEconomics?.lastSavedSettings);
      setEconomicsCurrency('cad');
      dispatch(setIsLoadingData(true));
    };

    async function compileNewEconomics(economics, settings) {
      try {
        const uwis = economics?.economicsWells?.wells?.map(well => well?.id);
        const listIds = economics?.economicsWells?.lists?.map(list => list?.id);
        const typeCurveIds = economics?.economicsWells?.typeCurves?.map(typeCurve => typeCurve?.id);
        const groupWellIds = economics?.economicsWells?.groupWells?.map(groupWell => groupWell?.id);
        
        const sourceData = await getSourceData(
          listIds,
          lists,
          uwis,
          setUwis,
          wells,
          typeCurveIds,
          typeCurves,
          groupWellIds,
          groupWells,
          settings
        );

        const newEconomics = {
          id: economics?.id,
          name: economics?.name,
          projectId: economics?.projectId,
          createdBy: economics?.createdBy,
          createdOn: economics?.createdOn,
          lastModified: new Date(),
          description: economics?.description,
          settings: {...settings, startDate: economicsStartDate},
          lastSavedSettings: economics?.lastSavedSettings,
          economicsWells: economics?.economicsWells,
          economicsIds: economics?.economicsIds
        };

        return { sourceData: sourceData, economics: newEconomics };
      } catch (error) {
        return {};
      };
    };

    async function handleCalculateEconomics(settings) {
      try {
        dispatch(setIsLoadingData(true));

        let sourceData = null;
        let economicsAnalyses = null;
        let newEconomics = {...currentEconomics, settings};

        if (currentEconomics?.economicsWells && Object.keys(currentEconomics?.economicsWells)?.length > 0) {
          const compiledResult = await compileNewEconomics(currentEconomics, settings);
          sourceData = compiledResult?.sourceData;
          newEconomics = compiledResult?.economics;  
        } else if (currentEconomics?.economicsIds?.length > 0) {
          currentEconomics?.economicsIds.forEach(economicsId => {
            if (economics?.byId?.[economicsId]?.cashflowNet && economics?.byId?.[economicsId]?.forecastNet) {
              if (!economicsAnalyses) {
                economicsAnalyses = [];
              };
              economicsAnalyses.push({
                cashflowNet: economics?.byId?.[economicsId]?.cashflowNet, 
                cashflowGross: economics?.byId?.[economicsId]?.cashflowGross, 
                forecastNet: economics?.byId?.[economicsId]?.forecastNet,
                forecastGross: economics?.byId?.[economicsId]?.forecastGross,
                settings: economics?.byId?.[economicsId]?.settings
              });
            };
          });
        };

        if (socket && socket.readyState === 1) {
          socket.send(JSON.stringify({
            messageType: 'economics', 
            payload: {sourceData, economics: newEconomics, economicsAnalyses}, 
            userEmail: user?.value?.email
          }));
        };
        
      } catch (error) {
        console.log(error);
      };
    };

    async function handleCurrencyChange(event, newCurrency) {
      try {
        if (!newCurrency) return;

        let newSettings = {...economicsSettings};
        newSettings.currency = newCurrency;

        setEconomicsSettings(newSettings);
        setEconomicsCurrency(newCurrency);

        handleCalculateEconomics(newSettings);
      } catch (error) {
        console.log(error);
        return;
      };
    };

    async function handleUnitsChange(event, newUnits) {
      try {
        if (!newUnits) return;

        let newSettings = {...currentEconomics?.settings};
        newSettings.units = newUnits;

        setEconomicsSettings(newSettings);
        setEconomicsUnits(newUnits);

      } catch (error) {
        console.log(error);
        return;
      };
    };

    function handlePriceDeckChange(newPriceDeckName) {
      try {
        dispatch(setCurrentPriceDeck({ priceDeckName: newPriceDeckName, economicsId: currentEconomics?.id }));
      } catch (error) {
        console.log(error);
        return;
      };
    };

    function handleExpand(name) {
      let newExpanded = [...expanded];
      const indexOf = expanded.indexOf(name);
      if (indexOf !== -1) {
        newExpanded.splice(indexOf, 1);
        setExpanded(newExpanded);
      } else {
        newExpanded.push(name);
        setExpanded(newExpanded);
      };
    };

    function handleExpandAll() {
      if (expandAll) {
        setExpanded([]);
        setExpandAll(false);
      } else {
        setExpanded(() => grouping.map(g => g.name));
        setExpandAll(true);
      };
    };
    
  } catch (error) {
    console.log(error);
    return (<></>);
  };
};
